import React, { useEffect, useState } from 'react';
import { getAuth, createUserWithEmailAndPassword, sendPasswordResetEmail } from 'firebase/auth';
import {
  collection,
  getDocs,
  setDoc,
  doc,
  updateDoc,
  deleteDoc
} from 'firebase/firestore';
import { db } from '../firebase';

export default function AdminView() {
  const auth = getAuth();

  const [projects, setProjects] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  const [newEmail, setNewEmail] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [newAllowed, setNewAllowed] = useState([]);
  const [newRole, setNewRole] = useState('user');

  // Modal de seleção de projetos
  const [modalOpen, setModalOpen] = useState(false);
  const [modalUser, setModalUser] = useState(null);
  const [modalSelection, setModalSelection] = useState([]);

  // Carrega projetos e usuários
  useEffect(() => {
    const fetchData = async () => {
      try {
        const projSnap = await getDocs(collection(db, 'projects'));
        setProjects(projSnap.docs.map(d => ({ id: d.id, ...d.data() })));

        const usersSnap = await getDocs(collection(db, 'users'));
        setUsers(usersSnap.docs.map(d => ({ uid: d.id, ...d.data() })));
      } catch (err) {
        console.error('Erro AdminView:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Refaz lista de usuários
  const refreshUsers = async () => {
    const usersSnap = await getDocs(collection(db, 'users'));
    setUsers(usersSnap.docs.map(d => ({ uid: d.id, ...d.data() })));
  };

  // Cria novo usuário
  const handleCreateUser = async () => {
    if (!newEmail || !newPassword) return alert('Preencha email e senha');
    try {
      const { user } = await createUserWithEmailAndPassword(auth, newEmail, newPassword);
      await setDoc(doc(db, 'users', user.uid), {
        email: newEmail,
        allowedProjects: newAllowed,
        role: newRole
      });
      await refreshUsers();
      setNewEmail('');
      setNewPassword('');
      setNewAllowed([]);
      setNewRole('user');
    } catch (err) {
      console.error(err);
      alert('Erro ao criar usuário: ' + err.message);
    }
  };

  // Exclui usuário
  const deleteUser = async (uid) => {
    if (!window.confirm('Deseja realmente excluir este usuário?')) return;
    await deleteDoc(doc(db, 'users', uid));
    await refreshUsers();
  };

  // Enviar e-mail de redefinição de senha
  const handlePasswordReset = async (email) => {
    try {
      await sendPasswordResetEmail(auth, email);
      alert(`E-mail de redefinição de senha enviado para ${email}`);
    } catch (error) {
      console.error('Erro ao enviar redefinição de senha:', error);
      alert('Erro ao enviar redefinição de senha.');
    }
  };

  // Abre modal de projetos para usuário
  const openProjectsModal = (uid) => {
    const usr = users.find(u => u.uid === uid);
    setModalUser(uid);
    setModalSelection(usr.allowedProjects || []);
    setModalOpen(true);
  };

  // Confirma seleção e salva
  const handleModalConfirm = async () => {
    if (!modalUser) return;
    try {
      await updateDoc(doc(db, 'users', modalUser), { allowedProjects: modalSelection });
      await refreshUsers();
    } catch (err) {
      console.error('Erro ao atualizar projetos do usuário:', err);
      alert('Falha ao salvar');
    } finally {
      setModalOpen(false);
      setModalUser(null);
      setModalSelection([]);
    }
  };

  // Fecha modal sem salvar
  const handleModalCancel = () => {
    setModalOpen(false);
    setModalUser(null);
    setModalSelection([]);
  };

  if (loading) return <p className="text-center mt-20">Carregando administração…</p>;

  return (
    <div className="p-4 max-w-4xl mx-auto space-y-6">
      <h1 className="text-2xl font-bold">Administração de Usuários</h1>

      {/* Criar usuário */}
      <div className="bg-white p-4 rounded shadow space-y-2">
        <h2 className="font-semibold">Criar Novo Usuário</h2>
        <input
          type="email"
          placeholder="Email"
          value={newEmail}
          onChange={e => setNewEmail(e.target.value)}
          className="border px-2 py-1 rounded w-full"
        />
        <input
          type="password"
          placeholder="Senha"
          value={newPassword}
          onChange={e => setNewPassword(e.target.value)}
          className="border px-2 py-1 rounded w-full"
        />
        <div>
          <label className="font-medium">Tipo de Usuário:</label>
          <select
            value={newRole}
            onChange={e => setNewRole(e.target.value)}
            className="border px-2 py-1 rounded w-full"
          >
            <option value="user">User</option>
            <option value="admin">Admin</option>
          </select>
        </div>
        <div>
          <label className="font-medium">Projetos Permitidos:</label>
          <select
            multiple
            value={newAllowed}
            onChange={e =>
              setNewAllowed([...e.target.selectedOptions].map(o => o.value))
            }
            className="border px-2 py-1 rounded w-full"
          >
            {projects.map(p => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </select>
        </div>
        <button
          onClick={handleCreateUser}
          className="bg-blue-600 text-white px-4 py-2 rounded"
        >
          Criar Usuário
        </button>
      </div>

      {/* Usuários existentes */}
      <div className="bg-white p-4 rounded shadow">
        <h2 className="font-semibold mb-2">Usuários Existentes</h2>
        <table className="w-full table-auto border-collapse">
          <thead>
            <tr>
              <th className="border p-2">Email</th>
              <th className="border p-2">Tipo</th>
              <th className="border p-2">Projetos Permitidos</th>
              <th className="border p-2">Ações</th>
            </tr>
          </thead>
          <tbody>
            {users.map(u => (
              <tr key={u.uid}>
                <td className="border p-2">{u.email}</td>
                <td className="border p-2">{u.role || 'user'}</td>
                <td className="border p-2">
                  {(u.allowedProjects || [])
                    .map(pid => projects.find(p => p.id === pid)?.name || pid)
                    .join(', ') || <span className="text-gray-500">nenhum</span>}
                </td>
                <td className="border p-2 space-y-1">
                  <button
                    onClick={() => openProjectsModal(u.uid)}
                    className="bg-purple-500 hover:bg-purple-600 text-white px-2 py-1 rounded text-sm block w-full"
                  >
                    Projetos
                  </button>
                  <button
                    onClick={() => handlePasswordReset(u.email)}
                    className="bg-yellow-500 hover:bg-yellow-600 text-white px-2 py-1 rounded text-sm block w-full"
                  >
                    Enviar nova senha
                  </button>
                  <button
                    onClick={() => deleteUser(u.uid)}
                    className="bg-red-500 hover:bg-red-600 text-white px-2 py-1 rounded text-sm block w-full"
                  >
                    Excluir Usuário
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Modal de seleção de projetos */}
      {modalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center">
          <div className="bg-white p-6 rounded-lg shadow-lg w-96">
            <h3 className="text-lg font-semibold mb-4">Projetos Permitidos</h3>
            <div className="max-h-60 overflow-auto mb-4">
              {projects.map(p => (
                <label key={p.id} className="flex items-center space-x-2 mb-1">
                  <input
                    type="checkbox"
                    value={p.id}
                    checked={modalSelection.includes(p.id)}
                    onChange={e => {
                      const val = e.target.value;
                      setModalSelection(sel =>
                        sel.includes(val)
                          ? sel.filter(x => x !== val)
                          : [...sel, val]
                      );
                    }}
                  />
                  <span>{p.name}</span>
                </label>
              ))}
            </div>
            <div className="flex justify-end space-x-2">
              <button
                onClick={handleModalCancel}
                className="px-4 py-2 rounded border"
              >
                Cancelar
              </button>
              <button
                onClick={handleModalConfirm}
                className="px-4 py-2 rounded bg-blue-600 text-white"
              >
                Salvar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
